/**
 * Что раздел «Смена» спрашивает у `ops-api` — этап P5 (§2, §3 ТЗ портала).
 *
 * Адреса — `services/ops/api/routers/portal_shift.py`; правила за ними —
 * функции сервиса `ops`, те же, что у кнопок бота. Чек-листы здесь только
 * смотрят: проходят их в операционном боте.
 */

import { request, upload } from '../../api';

export interface ShiftAccess {
  today: string;
  checklists: boolean;
  checklists_all: boolean;
  tasks: boolean;
  tasks_all: boolean;
  timesheet: boolean;
  timesheet_edit: boolean;
  /** Таблица часов — отдельное право (D214). */
  timesheet_table: boolean;
  /** «Цеха и завтраки» — своя галочка (D235): видеть и править. */
  timesheet_setup: boolean;
  timesheet_setup_edit: boolean;
  /** «Дисциплина» — своя галочка (D235). */
  discipline: boolean;
  templates: boolean;
  templates_edit: boolean;
  summary: boolean;
}

export interface RunRow {
  id: number;
  template_id: number;
  title: string;
  date: string;
  area_id: number | null;
  area: string | null;
  position: string | null;
  window_from: string;
  window_to: string;
  status: string;
  status_title: string;
  performer: string | null;
  answered: number;
  total: number;
  started_at: string | null;
  finished_at: string | null;
}

export interface ChecklistDay {
  date: string;
  today: string;
  whole_shift: boolean;
  totals: { planned: number; done: number; done_late: number; missed: number; open: number };
  areas: { area_id: number | null; title: string; runs: RunRow[] }[];
}

export interface PhotoRef {
  photo_id: number;
  url: string | null;
  available: boolean;
  received_at: string | null;
  forwarded: boolean;
  width: number | null;
  height: number | null;
}

export interface RunItemRow {
  id: number;
  order_no: number;
  title: string;
  hint: string | null;
  field_type: string | null;
  is_critical: boolean;
  photo_required: boolean;
  allowed: string | null;
  state: 'done' | 'cant' | 'na' | null;
  state_title: string | null;
  value: string | null;
  comment: string | null;
  answered_at: string | null;
  problem: string | null;
  photos: PhotoRef[];
  tasks: { id: number; title: string; status: string; status_title: string }[];
}

export interface RunDetail {
  run: RunRow;
  note: string;
  items: RunItemRow[];
}

export interface TaskRow {
  id: number;
  title: string;
  description: string | null;
  status: string;
  status_title: string;
  active: boolean;
  area: string | null;
  position: string | null;
  assignee: string | null;
  due_at: string | null;
  overdue: boolean;
  photo_required: boolean;
  from_checklist: boolean;
  created_at: string;
  created_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  comment: string | null;
}

export interface TaskBoard {
  whole: boolean;
  can_cancel: boolean;
  closed_days: number;
  columns: { open: TaskRow[]; in_progress: TaskRow[]; closed: TaskRow[] };
}

export type TaskAction = 'take' | 'resolve' | 'cancel';

export interface TaskCard {
  task: TaskRow;
  source: {
    run_id: number;
    checklist: string;
    date: string;
    item: string | null;
    may_open: boolean;
  } | null;
  history: { at: string | null; text: string }[];
  photos: { problem: PhotoRef[]; resolution: PhotoRef[] };
  actions: TaskAction[];
}

export interface PhotoAccepted {
  message: string;
  photo: PhotoRef;
}

export interface TaskAnswer {
  message: string;
  card: TaskCard;
}

export interface ShiftMark {
  id: number;
  employee_id: number;
  name: string;
  date: string;
  started_at: string;
  finished_at: string | null;
  break_minutes: number | null;
  open: boolean;
  worked_minutes: number | null;
  worked_text: string;
  note: string | null;
}

export interface MarkAnswer {
  message: string;
  shift: ShiftMark;
}

/** Как у должности показывается завтрак (D214): нет, часами (зал), единицей (кухня). */
export type BreakfastMode = 'none' | 'hours' | 'unit';

export interface SheetEntry {
  employee_id: number;
  name: string;
  position_id: number | null;
  position_name: string | null;
  home_position_id: number | null;
  breakfast_mode: BreakfastMode;
  /** «09:30» по часам ресторана. */
  started: string;
  finished: string | null;
  open: boolean;
  /** Часы строкой с запятой: «11,5». */
  total: string | null;
  regular: string | null;
  breakfast: string | null;
  /** Заполнено не в ресторане: «в 2,3 км от ресторана»; `null` — на месте. */
  outside: string | null;
  /** Вызывной, вписанный вручную (D266). */
  on_call?: boolean;
}

/** Кого можно добавить в лист этого дня — отметки у него ещё нет. */
export interface FreeMember {
  employee_id: number;
  name: string;
  position_id: number | null;
  /** «Как в прошлый раз» — время последней смены. */
  last: { started: string; finished: string } | null;
  /** Вызывной (D266): в строке «+» — группой «Вызывные». */
  on_call?: boolean;
}

/**
 * Ответ «вписать вручную» (D266): заведён — или тёзка уже есть, и решает
 * человек: «это она?».
 */
export type OnCallAnswer =
  | { status: 'created'; employee_id: number; full_name: string }
  | {
      status: 'same_name';
      matches: { employee_id: number; full_name: string; position_name: string | null }[];
    };

export interface PositionSetup {
  position_id: number;
  name: string;
  section: string | null;
  section_order: number;
  breakfast: BreakfastMode;
  /** Приход по правилу кухни (D219): галочка должности. */
  kitchen_arrival: boolean;
  department: string | null;
}

export interface DaySheet {
  date: string;
  today: string;
  can_edit: boolean;
  is_keeper: boolean;
  can_see_table: boolean;
  /** Присылать место телефона с каждой записью (D214). */
  geo_check: boolean;
  backfill_open: boolean;
  can_switch_backfill: boolean;
  sections: {
    title: string;
    position_ids: number[];
    /** Подразделение цеха: «Зал», «Кухня». */
    department: string | null;
    /** Все должности подразделения — кого можно поставить на подмену. */
    department_position_ids: number[];
    entries: SheetEntry[];
  }[];
  free: FreeMember[];
  positions: PositionSetup[];
}

export interface SheetInput {
  started: string;
  finished: string | null;
  position_id?: number | null;
  location?: { lat: number; lon: number; accuracy_m: number | null } | null;
}

export interface GridCell {
  started: string;
  finished: string | null;
  /**
   * Смена числом, как в таблице ресторана для зарплаты: 09:20–23:20 →
   * 9202320 (формат ячейки там показывает «09:20 - 23:20»). У смены без
   * ухода — null. Считает сервер, здесь только перенос.
   */
  payroll_number: number | null;
  regular: string | null;
  breakfast: string | null;
  /** Заполнено не в ресторане (D214). */
  outside: boolean;
  /** Правило прихода кухни сдвинуло приход (D219): 10:40 → 11:10. */
  ruled: boolean;
  /** Строка «по факту»: приход, как отметили, и все часы по отметкам. */
  fact_started: string;
  fact_hours: string | null;
}

export interface GridRow {
  employee_id: number;
  name: string;
  position_id: number | null;
  position_name: string | null;
  /** Вызывной (D266). */
  on_call?: boolean;
  breakfast_mode: BreakfastMode;
  cells: Record<string, GridCell>;
  regular_total: string;
  breakfast_total: string;
  /** Итог строки «по факту» — все часы по отметкам (D219). */
  fact_total: string;
  open_count: number;
}

export interface HoursGrid {
  from: string;
  to: string;
  today: string;
  days: string[];
  sections: { title: string; rows: GridRow[] }[];
  regular_total: string;
  breakfast_total: string;
  /** Правило прихода кухни, по которому посчитана таблица (D219). */
  kitchen_rule: { breakfast_from: string; breakfast_until: string; start: string } | null;
}

export interface PositionsSetup {
  can_edit: boolean;
  breakfast_from: string;
  breakfast_to: string;
  kitchen_breakfast_until: string;
  kitchen_start: string;
  positions: PositionSetup[];
}

export type TemplateStatus = 'draft' | 'changed' | 'published';

export interface TemplateSummary {
  id: number;
  title: string;
  area: { id: number; title: string } | null;
  version: number;
  published_version: number | null;
  status: TemplateStatus;
  status_title: string;
  is_active: boolean;
  items: number;
  schedules: string[];
  updated_at: string;
}

export interface TemplateList {
  can_edit: boolean;
  templates: TemplateSummary[];
  areas: { id: number; title: string }[];
  positions: { id: number; name: string }[];
  field_types: { code: string; title: string }[];
}

export interface TemplateItem {
  order_no: number;
  title: string;
  hint: string | null;
  field_type: string;
  field_type_title: string;
  photo_required: boolean;
  is_critical: boolean;
  num_min: string | null;
  num_max: string | null;
  num_unit: string | null;
  choice_options: string[] | null;
  assignee_position_id: number | null;
  assignee_position: string | null;
  allowed: string | null;
}

export interface ScheduleRow {
  id: number;
  weekdays: number[];
  weekdays_text: string;
  window_from: string;
  window_to: string;
  spans_midnight: boolean;
  window_text: string;
  positions: { id: number; name: string }[];
}

export interface PreviewItem {
  position: number;
  total: number;
  heading: string;
  title: string;
  hint: string | null;
  allowed: string | null;
  critical: boolean;
  field_type: string;
  choice_options: string[] | null;
  photo_required: boolean;
}

export interface TemplateDetail extends TemplateSummary {
  description: string | null;
  can_edit: boolean;
  item_list: TemplateItem[];
  schedule_list: ScheduleRow[];
  blockers: string[];
  can_publish: boolean;
  preview: PreviewItem[];
}

export interface ItemInput {
  title: string;
  field_type: string;
  hint?: string | null;
  photo_required?: boolean;
  is_critical?: boolean;
  num_min?: string | null;
  num_max?: string | null;
  num_unit?: string | null;
  choice_options?: string[] | null;
  assignee_position_id?: number | null;
}

export interface ScheduleInput {
  weekdays: number[];
  window_from: string;
  window_to: string;
  position_ids: number[];
}

export interface DisciplineTotals {
  planned: number;
  done_on_time: number;
  done_late: number;
  missed: number;
  items_total: number;
  items_problem: number;
  score: number | null;
}

export interface Discipline {
  today: string;
  period: { from: string; to: string };
  manager: boolean;
  target: number;
  my_runs: RunRow[];
  tasks: TaskRow[];
  day: (DisciplineTotals & { date: string; in_work: number; runs: RunRow[] }) | null;
  not_done: RunRow[] | null;
  score: DisciplineTotals | null;
  employees: (DisciplineTotals & { employee_id: number; name: string; done: number })[] | null;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text ? `?${text}` : '';
}

const BASE = '/api/ops/shift';

/** Полями объекта: тест подменяет ровно один адрес. */
export const shiftApi = {
  access: () => request<ShiftAccess>(`${BASE}/access`),
  checklists: (date?: string) => request<ChecklistDay>(`${BASE}/checklists${query({ date })}`),
  run: (id: number) => request<RunDetail>(`${BASE}/checklists/${id}`),
  tasks: (days = 7) => request<TaskBoard>(`${BASE}/tasks${query({ days })}`),
  task: (id: number) => request<TaskCard>(`${BASE}/tasks/${id}`),
  take: (id: number) => request<TaskAnswer>(`${BASE}/tasks/${id}/take`, { method: 'POST' }),
  resolve: (id: number, comment: string, photoId: number | null = null) =>
    request<TaskAnswer>(`${BASE}/tasks/${id}/resolve`, {
      method: 'POST',
      body: { comment, photo_id: photoId },
    }),
  /** Снимок исправления с портала (D185): файл телом запроса. */
  taskPhoto: (id: number, file: Blob) => upload<PhotoAccepted>(`${BASE}/tasks/${id}/photo`, file),
  cancel: (id: number, reason: string) =>
    request<TaskAnswer>(`${BASE}/tasks/${id}/cancel`, { method: 'POST', body: { reason } }),
  /** Табель прихода (D214). */
  sheet: (date?: string) => request<DaySheet>(`${BASE}/timesheet/sheet${query({ date })}`),
  saveSheet: (date: string, employeeId: number, input: SheetInput) =>
    request<MarkAnswer>(`${BASE}/timesheet/sheet/${date}/${employeeId}`, {
      method: 'PUT',
      body: input,
    }),
  /** Вписать вызывного вручную — завести в справочнике платформы (D266). */
  addOnCall: (fullName: string, positionId: number, otherPerson = false) =>
    request<OnCallAnswer>('/api/platform/timesheet/on-call', {
      method: 'POST',
      body: { full_name: fullName, position_id: positionId, other_person: otherPerson },
    }),
  removeSheet: (date: string, employeeId: number) =>
    request<{ message: string }>(`${BASE}/timesheet/sheet/${date}/${employeeId}`, {
      method: 'DELETE',
    }),
  backfill: (open: boolean) =>
    request<{ backfill_open: boolean; message: string }>(`${BASE}/timesheet/backfill`, {
      method: 'PUT',
      body: { open },
    }),
  /** Таблица часов за период (D214). */
  grid: (from?: string, to?: string) =>
    request<HoursGrid>(`${BASE}/timesheet/grid${query({ from, to })}`),
  /** Адрес файла Excel — ссылкой: вход по куки, файл отдаёт сервер. */
  gridXlsxUrl: (from: string, to: string) => `${BASE}/timesheet/grid.xlsx${query({ from, to })}`,
  positionsSetup: () => request<PositionsSetup>(`${BASE}/timesheet/positions`),
  savePositionSetup: (
    positionId: number,
    setup: {
      section: string | null;
      section_order: number;
      breakfast: BreakfastMode;
      kitchen_arrival: boolean;
    },
  ) =>
    request<PositionsSetup>(`${BASE}/timesheet/positions/${positionId}`, {
      method: 'PUT',
      body: setup,
    }),
  templates: () => request<TemplateList>(`${BASE}/templates`),
  template: (id: number) => request<TemplateDetail>(`${BASE}/templates/${id}`),
  createTemplate: (title: string, areaId: number | null) =>
    request<TemplateDetail>(`${BASE}/templates`, {
      method: 'POST',
      body: { title, area_id: areaId },
    }),
  patchTemplate: (id: number, patch: { title?: string; area_id?: number; description?: string }) =>
    request<TemplateDetail>(`${BASE}/templates/${id}`, { method: 'PATCH', body: patch }),
  addItem: (id: number, item: ItemInput) =>
    request<TemplateDetail>(`${BASE}/templates/${id}/items`, { method: 'POST', body: item }),
  replaceItem: (id: number, orderNo: number, item: ItemInput) =>
    request<TemplateDetail>(`${BASE}/templates/${id}/items/${orderNo}`, {
      method: 'PUT',
      body: item,
    }),
  removeItem: (id: number, orderNo: number) =>
    request<TemplateDetail>(`${BASE}/templates/${id}/items/${orderNo}`, { method: 'DELETE' }),
  addSchedule: (id: number, schedule: ScheduleInput) =>
    request<TemplateDetail>(`${BASE}/templates/${id}/schedules`, {
      method: 'POST',
      body: schedule,
    }),
  setPositions: (id: number, scheduleId: number, positionIds: number[]) =>
    request<TemplateDetail>(`${BASE}/templates/${id}/schedules/${scheduleId}/positions`, {
      method: 'PUT',
      body: { position_ids: positionIds },
    }),
  removeSchedule: (id: number, scheduleId: number) =>
    request<TemplateDetail>(`${BASE}/templates/${id}/schedules/${scheduleId}`, {
      method: 'DELETE',
    }),
  publish: (id: number) =>
    request<{ message: string; template: TemplateDetail }>(`${BASE}/templates/${id}/publish`, {
      method: 'POST',
    }),
  discipline: (from?: string, to?: string) =>
    request<Discipline>(`${BASE}/discipline${query({ from, to })}`),
};

/** Перечитывание: при возврате на вкладку и раз в 30 секунд (§3 ТЗ портала). */
export const LIVE = { refetchOnWindowFocus: true, refetchInterval: 30_000 } as const;
